<template>
  <div
    v-if="videoUrl || fileMetadata"
    class="video-preview"
    @click.stop
    @mousedown.stop
  >
    <VideoEmbedPreview
      v-if="videoUrl && !fileMetadata"
      :key="videoUrl"
      :videoUrl="videoUrl"
      :playerId="playerId"
      @showVideo="onOpenLinkedVideo"
      @click.stop
    />
    <img
      v-else-if="showThumbnail"
      :src="thumbnailUrl"
      :alt="fileMetadata?.name ?? 'Video preview'"
      class="video-preview__thumbnail"
      @click.stop.prevent="onOpenVideoFile"
      @error="onThumbnailError"
    />
    <VideoAttachmentItem
      v-else-if="fileMetadata"
      :key="currentVideoUrl"
      :file="fileMetadata"
      :videoPath="currentVideoUrl"
      :hidePoster="true"
      :hideInfo="true"
      :fancyboxData="fileMetadata.id"
      @click.stop.prevent="onOpenVideoFile"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, ref, watch } from "vue"
import { useStore } from "vuex"

import VideoAttachmentItem from "@/components/VideoPlayer/VideoAttachmentItem.vue"
import VideoEmbedPreview from "@/components/VideoPlayer/VideoEmbedPreview.vue"

import { getVideoEmbedUrl, isValidParsedVideo, parseVideoUrl } from "@/helpers/parseVideoUrl"
import { FileMetaData } from "@/models"
import { Actions } from "@/store"

import type { PropType } from "vue"

defineOptions({
  name: "VideoPreview"
})

const props = defineProps({
  playerId: {
    type: String,
    default: ""
  },
  videoUrl: {
    type: String,
    default: ""
  },
  fileMetadata: {
    type: Object as PropType<FileMetaData | null>,
    default: null
  },
  projectId: {
    type: String,
    default: ""
  },
  thumbnailUrl: {
    type: String,
    default: ""
  }
})

const store = useStore()
const instance = getCurrentInstance()

const currentVideoUrl = ref("")
const hasFileVideo = computed(() => !!props.fileMetadata?.id)
const thumbnailFailed = ref(false)
const showThumbnail = computed(
  () => Boolean(props.thumbnailUrl) && hasFileVideo.value && !thumbnailFailed.value
)

const fetchCurrentVideoUrl = async () => {
  if (!hasFileVideo.value || !props.fileMetadata) return

  const payload = props.projectId
    ? {
        projectId: props.projectId,
        attachmentId: props.fileMetadata.id
      }
    : {
        attachmentId: props.fileMetadata.id
      }

  const response = (await store.dispatch(Actions.ATTACHMENT_VIDEO_URL, payload)) as string

  currentVideoUrl.value = response
}

const onThumbnailError = async () => {
  thumbnailFailed.value = true
  if (!currentVideoUrl.value) {
    await fetchCurrentVideoUrl()
  }
}

const openVideoPreview = ({
  type,
  src,
  caption = "",
  fileMetadataId = ""
}: {
  type: "iframe" | "video" | "html5video"
  src: string
  caption?: string
  fileMetadataId?: string
}) => {
  if (!src) return

  instance?.proxy?.$fancybox.show(
    [
      {
        type,
        src,
        caption,
        fileMetadataId,
        projectId: props.projectId
      }
    ],
    {
      Toolbar: {
        autoEnable: false
      }
    }
  )
}

const onOpenLinkedVideo = () => {
  if (!props.videoUrl) return

  const parsedVideo = parseVideoUrl(props.videoUrl)
  if (!isValidParsedVideo(parsedVideo)) return

  const src = parsedVideo.provider === "loom" ? getVideoEmbedUrl(props.videoUrl) : props.videoUrl

  openVideoPreview({
    type: parsedVideo.provider === "loom" ? "iframe" : "video",
    src
  })
}

const onOpenVideoFile = async () => {
  await fetchCurrentVideoUrl()

  if (!currentVideoUrl.value) return

  openVideoPreview({
    type: "html5video",
    src: currentVideoUrl.value,
    caption: props.fileMetadata?.name ?? "",
    fileMetadataId: props.fileMetadata?.id ?? ""
  })
}

const openVideo = () => (props.fileMetadata ? onOpenVideoFile() : onOpenLinkedVideo())

defineExpose({ openVideo })

watch(
  [() => props.fileMetadata?.id, () => props.projectId, () => props.thumbnailUrl],
  async ([attachmentId]) => {
    thumbnailFailed.value = false

    if (!attachmentId) {
      currentVideoUrl.value = ""
      return
    }

    if (showThumbnail.value) {
      currentVideoUrl.value = ""
      return
    }

    await fetchCurrentVideoUrl()
  },
  {
    immediate: true
  }
)
</script>
